import { CARD_STATUS_LABELS, type ApprovalCardView } from '@evi/shared';

/**
 * Approval cards in plain words (Warren, 29 Oct 2026): who, how much, how old, when it expires, and the line after a
 * decision. Pure. Run: npx tsx --test src/lib/approvalsView.test.ts (from apps/admin).
 */
export function ageLabel(iso: string, now: Date): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - Date.parse(iso)) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function expiryLabel(iso: string, now: Date): string {
  const hours = Math.floor((Date.parse(iso) - now.getTime()) / 3_600_000);
  if (hours < 0) return 'expired';
  if (hours < 24) return `expires in ${Math.max(1, hours)} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `expires in ${days} day${days === 1 ? '' : 's'}`;
}

/** The driver, as the card names them: "Gery Example (gery@example.com)", the email alone, or nobody. */
export function cardWho(c: Pick<ApprovalCardView, 'subject'>): string | null {
  const { name, email } = c.subject;
  if (name && email) return `${name} (${email})`;
  return name || email || null;
}

/** The sentence the confirm sheet repeats, or a plain line when nothing moves. */
export function cardMoneySentence(c: Pick<ApprovalCardView, 'money'>): string {
  return c.money?.sentence ?? 'No money moves.';
}

/** What Decline does: nothing moves, or, on a card whose first step already ran, that step is given back. */
export function declineSentence(c: Pick<ApprovalCardView, 'partial'>): string {
  return c.partial ? c.partial.give_back : 'Decline it. Nothing moves.';
}

/** When a waiting card expires; a card whose first step already ran never does. */
export function waitingLabel(c: Pick<ApprovalCardView, 'partial' | 'expires_at'>, now: Date): string {
  return c.partial ? 'it does not expire, because a step already ran' : expiryLabel(c.expires_at, now);
}

/** The line a decided card shows. */
export function resultLine(c: Pick<ApprovalCardView, 'status' | 'decided_by' | 'result' | 'note' | 'superseded_by'>): string {
  const by = c.decided_by?.name ? ` by ${c.decided_by.name}` : '';
  switch (c.status) {
    case 'done': return `Approved${by}. Done.`;
    case 'declined': return `Declined${by}.${c.result?.startsWith('Declined: given back') ? ' Given back to the wallet.' : ''}${c.note ? ` ${c.note}` : ''}`;
    case 'failed': return `Approved${by}, then it failed. ${c.result ?? ''}`.trim();
    case 'stale': return c.superseded_by ? 'The facts changed, so a new card replaced this one. Nothing moved.' : 'The facts changed. Nothing moved.';
    case 'expired': return 'Expired after 7 days. Nothing moved.';
    case 'executing': return `Approved${by}. Running now.`;
    default: return CARD_STATUS_LABELS[c.status];
  }
}
